import React, { useEffect, useState } from 'react';
import { Routes, Route, useParams, useResolvedPath } from 'react-router-dom';
import { querystring } from '../../utils/helpers';
import AssessmentsAnswerView from '../Assessments/AssessmentsAnswerView';
import { useStores } from '../../hooks/use-stores';
import { observer } from 'mobx-react';
import PackageLoader from './PackageLoader';
import { createScormAdapter } from './content-scorm-bridge';
import { useTranslation } from 'react-i18next';

function getQueryParam(name) {
    const params = new URLSearchParams(window.location.search);
    return params.get(name);
}

function ScormHome() {
    const { companyId, uniqueId, accessKey, auId } = useParams();
    const resolved = useResolvedPath('.');
    const baseUrl = resolved.pathname;
    const { authStore, cmi5Store, brandingStore } = useStores();
    const [ready, setReady] = useState();
    const [bridgeError, setBridgeError] = useState(null);
    const { cmiUser } = cmi5Store;
    const { t } = useTranslation();

    // eslint-disable-next-line react-hooks/exhaustive-deps
    useEffect(() => {
        // Company context must be set before we signal ready, and the SCORM adapter must be
        // initialized before ready too — otherwise PackageLoader renders and calls
        // loadTraining before scormRegistration is known, which sends registration=null
        // to the server and loses progress (especially visible on F5/page refresh).
        // For the in-house bridge path, createScormAdapter is async (warmCache via
        // postMessage), so we must await it before setting ready=true.
        async function setup() {
            try {
                cmi5Store.setCompanyId(companyId);
                cmi5Store.setContext({ accessKey, auId: Number(auId) });
                brandingStore.setCustomBranding(companyId, accessKey);
                await initScorm();
                if (cmi5Store.scorm) {
                    setReady(true);
                }
            } catch (e) {
                console.error('[SCORM] setup failed:', e);
                setBridgeError(
                    t('Failed to initialize. Please try again or contact support.')
                );
            }
        }
        setup();
    }, []);

    const initScorm = async () => {
        const bridgeToken = getQueryParam('bridgeToken');
        const parentOriginHint = getQueryParam('parentOriginHint');

        if (bridgeToken) {
            const parentOrigin = parentOriginHint || (document.referrer ? new URL(document.referrer).origin : '*');
            try {
                const adapter = await createScormAdapter(parentOrigin);
                authStore.forgetUser();
                cmi5Store.setScorm(adapter);
            } catch (e) {
                console.error('[SCORM] In-house bridge failed:', e);
                setBridgeError(
                    t('Unable to connect to LMS. Please try again or contact support.')
                );
            }
            return;
        }

        let RXD;
        try {
            RXD = window.parent.RXD;
        } catch (e) {
            // cross-origin parent — RXD not accessible
        }
        if (RXD) {
            authStore.forgetUser();
            cmi5Store.setScorm(RXD);
        } else {
            setBridgeError(
                t('No SCORM adapter found. Please relaunch from your LMS.')
            );
        }
    };

    const AssessmentAnswersViewWrapper = () => {
        let questionIndex = querystring('index');
        return (
            <AssessmentsAnswerView
                companyId={companyId}
                assessmentId={uniqueId}
                currentUser={cmiUser}
                questionStore={cmi5Store}
                assessmentStore={cmi5Store}
                index={questionIndex}
                parentUrl={baseUrl}
            />
        );
    };

    if (bridgeError) {
        return (
            <div
                role="alert"
                style={{ padding: '2rem', textAlign: 'center', color: '#c00' }}
            >
                {bridgeError}
            </div>
        );
    }

    return (
        ready && (
            <Routes>
                <Route
                    path="assessment/answers"
                    element={<AssessmentAnswersViewWrapper />}
                />

                <Route
                    path=":id/answers"
                    element={<AssessmentAnswersViewWrapper />}
                />
                <Route
                    path="*"
                    element={
                        <PackageLoader
                            companyId={companyId}
                            uniqueId={uniqueId}
                            accessKey={accessKey}
                            auId={auId}
                        />
                    }
                />
            </Routes>
        )
    );
}

export default observer(ScormHome);
